import React from 'react';
import { AREA_LABELS, AREA_ORDER, SEVERITY_HINT, SEVERITY_LABELS } from '../labels.ts';
import type { HygieneArea, HygieneSeverity, HygieneSummary } from '../../../request/hygiene/hygieneRequests.ts';

const TILE_BAR: Record<string, string> = {
  high: 'bg-red-500',
  medium: 'bg-amber-400',
  low: 'bg-slate-300',
  ai: 'bg-blue-500',
};

function Tile({
  label,
  hint,
  value,
  bar,
  pressed,
  onClick,
}: {
  label: string;
  hint: string;
  value: number;
  bar: string;
  pressed: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      aria-label={`${label}, ${hint}: ${value}`}
      onClick={onClick}
      className={`relative overflow-hidden rounded-xl border bg-white px-4 py-3 text-left shadow-sm transition hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
        pressed ? 'border-blue-500 ring-1 ring-blue-500' : 'border-gray-200'
      }`}
    >
      <span className={`absolute inset-y-0 left-0 w-1 ${bar}`} aria-hidden />
      <span className="block text-xs text-gray-500">
        {label} · {hint}
      </span>
      <span className="mt-1 block text-2xl font-semibold text-gray-900">{value}</span>
    </button>
  );
}

export function HygieneSummaryBar({
  summary,
  severity,
  area,
  onSeverity,
  onArea,
  aiSelected,
  onAITiles,
}: {
  summary: HygieneSummary;
  severity: HygieneSeverity | '';
  area: HygieneArea | '';
  onSeverity: (s: HygieneSeverity | '') => void;
  onArea: (a: HygieneArea | '') => void;
  aiSelected: boolean;
  onAITiles: () => void;
}) {
  const sev: HygieneSeverity[] = ['high', 'medium', 'low'];
  const counts: Record<HygieneSeverity, number> = { high: summary.high, medium: summary.medium, low: summary.low };
  const areas = new Map(summary.areas.map((a) => [a.area, a]));
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {sev.map((s) => (
          <Tile
            key={s}
            label={SEVERITY_LABELS[s]}
            hint={SEVERITY_HINT[s]}
            value={counts[s]}
            bar={TILE_BAR[s]}
            pressed={severity === s}
            onClick={() => onSeverity(severity === s ? '' : s)}
          />
        ))}
        <Tile label="AI suggestions" hint="to review" value={summary.ai_suggestions} bar={TILE_BAR.ai} pressed={aiSelected} onClick={onAITiles} />
      </div>

      <div>
        <p className="mb-2 text-xs font-medium text-gray-500">By area — select one to filter</p>
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-5 lg:grid-cols-9">
          {AREA_ORDER.map((a) => {
            const c = areas.get(a) ?? { high: 0, medium: 0, low: 0 };
            const worst = c.high > 0 ? 'border-t-red-500' : c.medium > 0 ? 'border-t-amber-400' : c.low > 0 ? 'border-t-slate-300' : 'border-t-green-500';
            const text = c.high > 0 ? `${c.high} high` : c.medium > 0 ? `${c.medium} medium` : c.low > 0 ? `${c.low} low` : 'Clear';
            const pressed = area === a;
            return (
              <button
                key={a}
                type="button"
                aria-pressed={pressed}
                aria-label={`${AREA_LABELS[a]}: ${text}`}
                onClick={() => onArea(pressed ? '' : a)}
                className={`rounded-lg border border-t-4 bg-white px-3 py-2 text-left text-sm shadow-sm transition hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 ${worst} ${
                  pressed ? 'ring-1 ring-blue-500' : ''
                }`}
              >
                <span className="block font-medium text-gray-900">{AREA_LABELS[a]}</span>
                <span className="block text-xs text-gray-500">{text}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
